import { cn } from '@/lib/utils';
import React from 'react';
import { Skeleton } from './common/Skeleton';

/**
 * Content-shaped placeholder for `Schedule` while location data loads.
 * Mirrors the Big Numbers wizard so the swap to the real picker
 * doesn't reflow: oversized step numeral on the left, label + content
 * skeleton on the right, divider-separated rows.
 */
const StepSkeleton: React.FC<{ index: number; total: number; tall?: boolean }> = ({
  index,
  total,
  tall,
}) => (
  <li
    className={cn(
      'border-t border-gray-950/10 dark:border-white/10 py-6 sm:py-8',
      index === total - 1 && 'border-b'
    )}
  >
    <div className="flex items-start gap-6 sm:gap-10">
      {/* Step numeral — stays visible (it's just text), so we only
          skeleton the content alongside it. */}
      <span className="text-4xl sm:text-5xl font-semibold tabular-nums tracking-tight leading-none shrink-0 text-gray-300 dark:text-gray-700">
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-3">
        <Skeleton className="h-7 sm:h-8 w-32" />
        {tall && (
          <div className="flex flex-col gap-2.5 mt-3">
            <Skeleton className="h-14 w-full rounded-2xl" />
            <Skeleton className="h-14 w-full rounded-2xl" />
            <Skeleton className="h-14 w-3/4 rounded-2xl" />
          </div>
        )}
      </div>
    </div>
  </li>
);

const ScheduleSkeleton: React.FC<{ stepCount?: number }> = ({ stepCount = 3 }) => (
  <div className="flex flex-col gap-6" role="status" aria-label="Loading scheduling options">
    <ol role="list" className="flex flex-col">
      {Array.from({ length: stepCount }).map((_, i) => (
        <StepSkeleton
          key={i}
          index={i}
          total={stepCount}
          // First step is the active one — show body skeleton.
          // Remaining are locked, just header.
          tall={i === 0}
        />
      ))}
    </ol>
    <span className="sr-only">Loading scheduling options…</span>
  </div>
);

export default ScheduleSkeleton;
